import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Grid, TextField, Paper, IconButton, Chip, Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from 'sweetalert2';
import { api } from '../../../api';
import MDDatePicker from 'components/MDDatePicker';
import RichTextEditor from 'components/RichTextEditor';

const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return d; }
};


const emptyBaseline = () => ({
  scan_date: '',
  uterus: '',
  tubes: '',
  right_ovary_size: '',
  right_ovary: '',
  left_ovary_size: '',
  left_ovary: '',
  endometrium_size: '',
  remarks: '',
});

// Small labeled value display used inside scan cards
const InfoBlock = ({ label, value }) => (
  <Box sx={{ minWidth: 80 }}>
    <Typography sx={{ fontSize: '10px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.3 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>
      {value || '—'}
    </Typography>
  </Box>
);
InfoBlock.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) };

function USGSection({ procedureId, existingUsgs, canSave, canEdit, canDelete, onSaved }) {
  const theme = useTheme();
  const primaryColor = theme.palette.logo?.main || '#00798E';

  const [usgs, setUsgs] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(emptyBaseline());
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (existingUsgs?.length) setUsgs(existingUsgs);
  }, [existingUsgs]);

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'white',
      '&:hover fieldset': { borderColor: primaryColor },
      '&.Mui-focused fieldset': { borderColor: primaryColor, borderWidth: 2 },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: primaryColor },
  };
  const smallInput = { size: 'small', sx: inputSx };

  // ---- Save new scan ----
  const handleSave = async () => {
    if (!addForm.scan_date) {
      Swal.fire({ icon: 'warning', title: 'Date Required', text: 'Please enter the scan date.', timer: 2500, showConfirmButton: false });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        baseline_scan: 'Y',
        scan_date: addForm.scan_date,
        uterus: addForm.uterus || null,
        tubes: addForm.tubes || null,
        right_ovary_size: addForm.right_ovary_size || null,
        right_ovary: addForm.right_ovary || null,
        left_ovary_size: addForm.left_ovary_size || null,
        left_ovary: addForm.left_ovary || null,
        endometrium_size: addForm.endometrium_size || null,
        remarks: addForm.remarks || null,
      };
      const res = await api.ivf.createUsg(procedureId, payload);
      if (res?.data) setUsgs(prev => [...prev, res.data]);
      setAddForm(emptyBaseline());
      setShowAddForm(false);
      if (onSaved) onSaved();
      Swal.fire({ icon: 'success', title: 'Saved', text: 'Scan record saved.', timer: 2000, timerProgressBar: true, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  // ---- Edit ----
  const startEdit = (usg) => {
    setEditingId(usg.id);
    setEditForm({
      scan_date: usg.scan_date ? usg.scan_date.split('T')[0] : '',
      uterus: usg.uterus || '',
      tubes: usg.tubes || '',
      right_ovary_size: usg.right_ovary_size || '',
      right_ovary: usg.right_ovary || '',
      left_ovary_size: usg.left_ovary_size || '',
      left_ovary: usg.left_ovary || '',
      endometrium_size: usg.endometrium_size || '',
      remarks: usg.remarks || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm?.scan_date) {
      Swal.fire({ icon: 'warning', title: 'Date Required', text: 'Please enter the scan date.', timer: 2500, showConfirmButton: false });
      return;
    }
    setUpdating(true);
    try {
      const payload = {
        baseline_scan: 'Y',
        scan_date: editForm.scan_date,
        uterus: editForm.uterus || null,
        tubes: editForm.tubes || null,
        right_ovary_size: editForm.right_ovary_size || null,
        right_ovary: editForm.right_ovary || null,
        left_ovary_size: editForm.left_ovary_size || null,
        left_ovary: editForm.left_ovary || null,
        endometrium_size: editForm.endometrium_size || null,
        remarks: editForm.remarks || null,
      };
      const res = await api.ivf.updateUsg(editingId, payload);
      const updated = res?.data;
      setUsgs(prev => prev.map(u => u.id === editingId ? (updated || { ...u, ...payload }) : u));
      setEditingId(null);
      setEditForm(null);
      if (onSaved) onSaved();
      Swal.fire({ icon: 'success', title: 'Updated', text: 'Record updated.', timer: 2000, timerProgressBar: true, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Failed to update.' });
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (usg) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete Record',
      text: `Delete scan dated ${formatDate(usg.scan_date)}?`,
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;
    try {
      await api.ivf.deleteUsg(usg.id);
      setUsgs(prev => prev.filter(u => u.id !== usg.id));
      if (onSaved) onSaved();
      Swal.fire({ icon: 'success', title: 'Deleted', timer: 1800, timerProgressBar: true, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Failed to delete.' });
    }
  };

  // ---- Inline edit form (must NOT be a sub-component — causes focus loss) ----
  const renderEditForm = (usg) => {
    if (!editForm) return null;
    return (
      <Box sx={{ mt: 1.5, p: 2, backgroundColor: '#f0fafb', borderRadius: 1.5, border: `1.5px solid ${primaryColor}` }}>
        <Typography sx={{ fontWeight: 'bold', color: primaryColor, fontSize: '0.8rem', mb: 1.5 }}>✏️ Edit — {formatDate(usg.scan_date)}</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={3}>
            <MDDatePicker label="Scan Date *" value={editForm.scan_date || ''}
              onChange={d => setEditForm(p => ({ ...p, scan_date: d }))}
              textFieldProps={{ fullWidth: true, ...smallInput }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Uterus" fullWidth value={editForm.uterus}
              onChange={e => setEditForm(p => ({ ...p, uterus: e.target.value.toUpperCase() }))} {...smallInput} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Tubes" fullWidth value={editForm.tubes}
              onChange={e => setEditForm(p => ({ ...p, tubes: e.target.value.toUpperCase() }))} {...smallInput} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Endometrium (mm)" fullWidth value={editForm.endometrium_size}
              onChange={e => setEditForm(p => ({ ...p, endometrium_size: e.target.value.toUpperCase() }))} {...smallInput} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Right Ovary Size (mm)" fullWidth value={editForm.right_ovary_size}
              onChange={e => setEditForm(p => ({ ...p, right_ovary_size: e.target.value.toUpperCase() }))} {...smallInput} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Right Ovary AFC" fullWidth value={editForm.right_ovary}
              onChange={e => setEditForm(p => ({ ...p, right_ovary: e.target.value.toUpperCase() }))} {...smallInput} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Left Ovary Size (mm)" fullWidth value={editForm.left_ovary_size}
              onChange={e => setEditForm(p => ({ ...p, left_ovary_size: e.target.value.toUpperCase() }))} {...smallInput} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Left Ovary AFC" fullWidth value={editForm.left_ovary}
              onChange={e => setEditForm(p => ({ ...p, left_ovary: e.target.value.toUpperCase() }))} {...smallInput} />
          </Grid>
          <Grid item xs={12}>
            <RichTextEditor label="Remarks" value={editForm.remarks || ''}
              onChange={v => setEditForm(p => ({ ...p, remarks: v }))} height="100px" />
          </Grid>
        </Grid>
        <Box display="flex" justifyContent="flex-end" gap={1} mt={1.5}>
          <button type="button" onClick={() => { setEditingId(null); setEditForm(null); }} disabled={updating}
            style={{ padding: '7px 16px', background: 'transparent', color: '#6c757d', border: '1px solid #6c757d', borderRadius: 4, cursor: 'pointer', fontSize: '0.8rem' }}>
            Cancel
          </button>
          <button type="button" onClick={handleSaveEdit} disabled={updating}
            style={{ padding: '7px 18px', background: updating ? '#ccc' : '#28a745', color: '#fff', border: 'none', borderRadius: 4, cursor: updating ? 'not-allowed' : 'pointer', fontSize: '0.8rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5 }}>
            <SaveIcon sx={{ fontSize: '0.95rem' }} />
            {updating ? 'Saving...' : 'Save Changes'}
          </button>
        </Box>
      </Box>
    );
  };

  // ---- Scan card (view mode) ----
  const renderScanCard = (usg, index) => (
    <Paper key={usg.id} elevation={0} sx={{ border: `1px solid ${primaryColor}25`, borderLeft: `4px solid ${primaryColor}`, borderRadius: 1.5, p: 2, mb: 1.5, backgroundColor: '#fafffe' }}>
      {/* Card header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            label={`Scan #${index + 1}`}
            size="small"
            sx={{ backgroundColor: primaryColor, color: '#fff', fontWeight: 700, fontSize: '11px', height: 22, borderRadius: '4px' }}
          />
          <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#333' }}>
            {formatDate(usg.scan_date)}
          </Typography>
        </Box>
        <Box display="flex" gap={0.5}>
          {canEdit !== false && (
            <IconButton size="small" onClick={() => startEdit(usg)}
              sx={{ color: primaryColor, border: `1px solid ${primaryColor}40`, borderRadius: 1, width: 28, height: 28, '&:hover': { backgroundColor: `${primaryColor}12` } }} title="Edit">
              <EditIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          )}
          {canDelete !== false && (
            <IconButton size="small" onClick={() => handleDelete(usg)}
              sx={{ color: '#dc3545', border: '1px solid rgba(220,53,69,0.3)', borderRadius: 1, width: 28, height: 28, '&:hover': { backgroundColor: 'rgba(220,53,69,0.08)' } }} title="Delete">
              <DeleteIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Info row 1: Uterus, Tubes, Endometrium */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, px: 0.5, mb: 1.5 }}>
        <InfoBlock label="Uterus" value={usg.uterus} />
        <InfoBlock label="Tubes" value={usg.tubes} />
        <InfoBlock label="Endometrium" value={usg.endometrium_size} />
      </Box>
      {/* Info row 2: Ovary fields — Right | Left */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, px: 0.5 }}>
        <InfoBlock label="Right Ovary Size" value={usg.right_ovary_size} />
        <InfoBlock label="Right Ovary AFC" value={usg.right_ovary} />
        <Box sx={{ width: '1px', height: 'auto', borderLeft: '1px solid #e0e0e0', mx: 0.5, alignSelf: 'stretch' }} />
        <InfoBlock label="Left Ovary Size" value={usg.left_ovary_size} />
        <InfoBlock label="Left Ovary AFC" value={usg.left_ovary} />
      </Box>

      {/* Remarks */}
      {usg.remarks && (
        <>
          <Divider sx={{ my: 1.2 }} />
          <Typography sx={{ fontSize: '10px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.5 }}>Remarks</Typography>
          <Box sx={{ fontSize: '12px', color: '#444' }} dangerouslySetInnerHTML={{ __html: usg.remarks }} />
        </>
      )}

      {/* Inline edit form */}
      {editingId === usg.id && renderEditForm(usg)}
    </Paper>
  );

  return (
    <Box>
      <Paper sx={{ mb: 3, border: `1px solid ${primaryColor}30`, borderRadius: 2, overflow: 'hidden' }}>
        {/* Section header */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: `2px solid ${primaryColor}`, backgroundColor: `${primaryColor}0d`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography sx={{ color: primaryColor, fontWeight: 'bold', fontSize: '1rem' }}>Baseline Scan</Typography>
            {usgs.length > 0 && (
              <Chip label={`${usgs.length} record${usgs.length > 1 ? 's' : ''}`} size="small"
                sx={{ backgroundColor: '#d4edda', color: '#155724', fontSize: '11px', height: 20 }} />
            )}
          </Box>
          {canSave !== false && !showAddForm && (
            <button type="button" onClick={() => setShowAddForm(true)}
              style={{ padding: '6px 14px', background: 'transparent', color: primaryColor, border: `1px solid ${primaryColor}`, borderRadius: 4, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
              <AddIcon sx={{ fontSize: '1rem' }} /> Add Scan
            </button>
          )}
        </Box>

        <Box sx={{ p: 2 }}>
          {/* Existing scans */}
          {usgs.map((usg, idx) => renderScanCard(usg, idx))}

          {usgs.length === 0 && !showAddForm && (
            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px dashed #dee2e6', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">No scan records yet.</Typography>
            </Paper>
          )}

          {/* Add form */}
          {showAddForm && (
            <Paper elevation={0} sx={{ p: 2, border: `1.5px dashed ${primaryColor}`, borderRadius: 1.5, backgroundColor: '#fafafa', mt: usgs.length > 0 ? 1 : 0 }}>
              <Typography sx={{ fontWeight: 'bold', color: primaryColor, fontSize: '0.85rem', mb: 1.5 }}>New Baseline Scan</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6} sm={3}>
                  <MDDatePicker label="Scan Date *" value={addForm.scan_date || ''}
                    onChange={d => setAddForm(p => ({ ...p, scan_date: d }))}
                    textFieldProps={{ fullWidth: true, ...smallInput }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Uterus" fullWidth value={addForm.uterus}
                    onChange={e => setAddForm(p => ({ ...p, uterus: e.target.value.toUpperCase() }))} {...smallInput} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Tubes" fullWidth value={addForm.tubes}
                    onChange={e => setAddForm(p => ({ ...p, tubes: e.target.value.toUpperCase() }))} {...smallInput} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Endometrium (mm)" fullWidth value={addForm.endometrium_size}
                    onChange={e => setAddForm(p => ({ ...p, endometrium_size: e.target.value.toUpperCase() }))} {...smallInput} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Right Ovary Size (mm)" fullWidth value={addForm.right_ovary_size}
                    onChange={e => setAddForm(p => ({ ...p, right_ovary_size: e.target.value.toUpperCase() }))} {...smallInput} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Right Ovary AFC" fullWidth value={addForm.right_ovary}
                    onChange={e => setAddForm(p => ({ ...p, right_ovary: e.target.value.toUpperCase() }))} {...smallInput} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Left Ovary Size (mm)" fullWidth value={addForm.left_ovary_size}
                    onChange={e => setAddForm(p => ({ ...p, left_ovary_size: e.target.value.toUpperCase() }))} {...smallInput} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Left Ovary AFC" fullWidth value={addForm.left_ovary}
                    onChange={e => setAddForm(p => ({ ...p, left_ovary: e.target.value.toUpperCase() }))} {...smallInput} />
                </Grid>
                <Grid item xs={12}>
                  <RichTextEditor label="Remarks" value={addForm.remarks || ''}
                    onChange={v => setAddForm(p => ({ ...p, remarks: v }))} height="100px" />
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="flex-end" gap={1} mt={1.5}>
                <button type="button" onClick={() => { setShowAddForm(false); setAddForm(emptyBaseline()); }}
                  style={{ padding: '7px 16px', background: 'transparent', color: '#6c757d', border: '1px solid #6c757d', borderRadius: 4, cursor: 'pointer', fontSize: '0.8rem' }}>
                  Cancel
                </button>
                <button type="button" onClick={handleSave} disabled={saving}
                  style={{ padding: '7px 18px', background: saving ? '#ccc' : primaryColor, color: '#fff', border: 'none', borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.8rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <SaveIcon sx={{ fontSize: '0.95rem' }} />
                  {saving ? 'Saving...' : 'Save Scan'}
                </button>
              </Box>
            </Paper>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

USGSection.propTypes = {
  procedureId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  existingUsgs: PropTypes.array,
  canSave: PropTypes.bool,
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  onSaved: PropTypes.func,
};

USGSection.defaultProps = {
  existingUsgs: [],
  canSave: true,
  canEdit: true,
  canDelete: true,
  onSaved: null,
};

export default USGSection;
